import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import { Box, Divider } from "@mui/material";
import { ReactNode, useRef } from "react";
import { CSSProperties } from "styled-components";
import { GlobalModal } from "../../context/GlobalModalProvider";
import Flex from "../Flex";
import Text from "../Text";
import { useDrag } from "../draggable/use-drag";
import { DEFAULT_BG_COLOR } from "../../utils/utils";

type DraggableModalProps = GlobalModal & {
  onClose: () => void;
  onPin: () => void;
  children: ReactNode;
};

export const DraggableModal = ({
  children,
  onPin,
  onClose,
  title,
  initialPosition,
  width = "50vw",
  height,
  zIndex,
}: DraggableModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { position, handleMouseDown } = useDrag({
    ref: dialogRef,
    initialPosition,
  });

  const MINIMIZE_STYLE: CSSProperties = {
    position: "absolute",
    left: position.x,
    top: position.y,
    minWidth: 480,
    width: width,
    height: height,
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
    borderRadius: "8px",
    zIndex: (zIndex || 0) + 99,
  };

  return (
    <Box
      ref={dialogRef}
      style={MINIMIZE_STYLE}
      sx={{
        bgcolor: "white",
      }}
    >
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
        height={"40px"}
        px={1.8}
        sx={{ cursor: "move" }}
        borderRadius={"6px 6px 0px 0px"}
        bgcolor={DEFAULT_BG_COLOR}
        onMouseDown={(e) => {
          if (document.activeElement instanceof HTMLElement)
            document.activeElement.blur();

          handleMouseDown(e);
        }}
      >
        <Title title={title} />

        <Flex gap={1.5} alignItems={"center"} justifyContent={"center"}>
          <PushPinIcon
            fontSize="small"
            onClick={onPin}
            style={{ cursor: "pointer" }}
          />
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      </Flex>

      <Divider />

      <Box
        sx={{
          height: `calc(${height} - 40px)`,
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const Title = ({ title }: { title: ReactNode }) => {
  if (typeof title === "string")
    return (
      <Text fontSize={"16px"} fontWeight={"medium"}>
        {title}
      </Text>
    );

  return title;
};
